/** The sizes at indices `first` to `last`, both inclusive, must sum to at least `minimum`. */
export type SpanConstraint = {
  first: number;
  last: number;
  minimum: number;
};

const assertValidSpan = (
  count: number,
  { first, last }: SpanConstraint
): void => {
  if (Number.isInteger(first) && Number.isInteger(last)) {
    if (first >= 0 && first <= last && last < count) return;
  }
  throw new RangeError(`Invalid span [${first}, ${last}] for ${count} sizes`);
};

const toSizes = (prefixTotals: Float64Array): number[] =>
  Array.from(
    { length: prefixTotals.length - 1 },
    (_, index) => prefixTotals[index + 1] - prefixTotals[index]
  );

/**
 * The non-negative sizes with the smallest total that satisfy every
 * constraint. Every prefix total is also as small as it can be.
 */
export const minimumSpanSizes = (
  count: number,
  constraints: readonly SpanConstraint[]
): number[] => {
  constraints.forEach(constraint => assertValidSpan(count, constraint));
  const constraintsByEnd: SpanConstraint[][] = Array.from(
    { length: count + 1 },
    () => []
  );
  constraints.forEach(constraint =>
    constraintsByEnd[constraint.last + 1].push(constraint)
  );

  const prefixTotals = new Float64Array(count + 1);
  for (let end = 1; end <= count; end++) {
    prefixTotals[end] = constraintsByEnd[end].reduce(
      (total, { first, minimum }) =>
        Math.max(total, prefixTotals[first] + minimum),
      prefixTotals[end - 1]
    );
  }
  return toSizes(prefixTotals);
};
